import { Common, getCookie } from '../common';
import { Util } from '../util';
import { Names } from '../i18n';
import { DialogCreators } from '../dialog-creators';
import { StoreStausId, BahamasCoordinates, LoyaltyProvider } from '../enums';
import { BrandService } from '../brand/brand-service';
import { ILocation } from './interfaces/locations.interface';
import { IGetOfferUrlModel } from '../brand/interfaces/brand-service.interfaces';

import '../../css/shared/locations.css';
import { IaOLO } from '../interfaces/aolo.interface';

import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { IGeocodeResults } from './interfaces/geocode-result.interface';
import { IAddressFormat } from '../interfaces/global.interfaces';
/**
 * Class representing locations and stores.
 * @class
 */
export class Locations {
    /**
     * @private
     * @property {string[]} _zoneColors - An array of colors used for zone markers on the map.
     */
    private _zoneColors: string[] = ['#1E90FF', '#FF1493', '#32CD32', '#FF8C00', '#A0A0A0', '#28FFFF', '#FFD800', '#0026FF', '#B6FF00', '#FF00DC'];
    /**
     * @private
     * @property {ILocation[]} _locations - An array of location objects.
     */
    private _locations: ILocation[] = [];
    /**
     * @private
     * @property {number} _currentLat - The current latitude of the user.
     */
    private _currentLat: number = 0;
    /**
     * @private
     * @property {number} _currentLng - The current longitude of the user.
     */
    private _currentLng: number = 0;
    /**
     * @private
     * @property {any} _map - The map object.
     */
    private _map: any;
    /**
     * @private
     * @property {number} _defaultMapZoom - The default zoom level for the map.
     */
    private _defaultMapZoom: number = 12;
    /**
     * @private
     * @property {boolean} _mobileApp - Whether the app is being used on a mobile device.
     */
    private _mobileApp: boolean;
    private _pageFunction: Function | null;

    private _zipCode: string;

    /**
     * @private
     * @property {any} _markerCluster - The map marker cluster.
     */
    private _markerCluster: any;

    /**
     * @private
     * @property {any} _markers - The map markers.
     */
    private _markers: any;

    /**
     * @private
     * @property {any} _infoWindow - The map info window.
     */
    private _infoWindow: any;

    /**
     * Creates a new Locations object and initializes the list of store locations.
     * @constructor
     * @param {boolean} mobileApp - Whether the application is being accessed through a mobile device.
     * @param {number} mobileLat - The current latitude of the mobile device.
     * @param {number} mobileLng - The current longitude of the mobile device.
     */
    constructor(pageFunction: Function | null, mobileApp: boolean, locations: ILocation[]) {
        this._pageFunction = pageFunction;
        this._mobileApp = mobileApp;
        this._zipCode = '';
        this._locations = locations;
        const cookieLocation = getCookie('locationData');
        if (cookieLocation != null && cookieLocation != "") {
            this._currentLat = JSON.parse(cookieLocation).lat;
            this._currentLng = JSON.parse(cookieLocation).lng;
        }
        this._initLocations();
    }

    /**
     * Initializes the list of store locations.
     * @private
     * @async
     * @returns {Promise<void>}
     */
    private _initLocations = async (): Promise<void> => {
        this._setEventListeners();
    }
    public ViewLocations = async (): Promise<void> => {
        if (this._currentLat == 0 || this._currentLng == 0) {
            const cookieLocation = getCookie('locationData');
            if (cookieLocation != null && cookieLocation != "") {
                this._currentLat = JSON.parse(cookieLocation).lat;
                this._currentLng = JSON.parse(cookieLocation).lng;
                await this._renderPage(this._currentLat, this._currentLng);
            } else if (!this._mobileApp) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        try { document.cookie = `locationData={"lat":${position.coords.latitude},"lng":${position.coords.longitude}};max-age= 3600;path=/;domain=${window.location.hostname.split('.').reverse().splice(0, 2).reverse().join('.')}`; } catch { }
                        this._currentLat = position.coords.latitude;
                        this._currentLng = position.coords.longitude;
                        await this._renderPage(this._currentLat, this._currentLng);
                    },
                    async () => {
                        Util.setElement("value", "ddl_location_selector_country", aOLO.storeInfo.Country.IsoCode);
                        Util.DialogFadeIn('dia_locations_country_zip', aOLO);
                    });
            } else {
                Util.setElement("value", "ddl_location_selector_country", aOLO.storeInfo.Country.IsoCode);
                Util.DialogFadeIn('dia_locations_country_zip', aOLO);
            }
        } else {
            await this._renderPage(this._currentLat, this._currentLng);
        }
    }

    /**
     * Sets the event listeners for the search box.
     * @private
     * @returns {void}
     */
    private _setEventListeners = (): void => {
        const self = this;
        //Util.setElement("onblur", "txt_locations_search", () => { self._seachBlured(); });
        //Util.setElement("onfocus", "txt_locations_search", () => { self._seachFocused(); });
        //Util.setElement("oninput", "txt_locations_search", () => { self._search(); });
        Util.setElement("onclick", "btn_locations_close", () => { self.closeDialog(); });
        Util.setElement("onchange", "ddl_location_selector_country", () => { self.storeLocatorCountrySelectorChanged(); });
        Util.setElement("onclick", "btn_LocationSelector_ok", () => { self._validateZipCode(); });
        Util.setElement("onclick", "btn_LocationSelector_cancel", () => { self.closeDialog(); });
    }

    public closeDialog = async (): Promise<void> => {
        if (aOLO.isBrandSignIn) {
            if (this._pageFunction && this._mobileApp) {
                this._pageFunction("landing", false);
            } else if (this._pageFunction && !this._mobileApp) {
                let country = Util.getElementValue("ddl_location_selector_country");
                if (country == "BH") { //set lng/lat to bahamas if no zip code and is webview and is bahamas
                    this._currentLat = 25.0343;
                    this._currentLng = -77.3963;
                } else {
                    this._currentLat = 37.090200;
                    this._currentLng = -95.712900;
                }
                await this._renderPage(this._currentLat, this._currentLng);
                if (country == "US")
                    this._map.setZoom(4);
            }
            Util.DialogFadeOut('dia_locations_country_zip');
            Util.DialogFadeOut("dia_locations", false);

        } else {
            if (this._pageFunction)
                this._pageFunction("signin", false);
            //hideElement("dia_locations"); 
            Util.DialogFadeOut('dia_locations_country_zip');
            Util.DialogFadeOut("dia_locations", false);
        }
    }

    /**\
     * 
     */
    public storeLocatorCountrySelectorChanged = (): void => {
        const country = Util.getElementValue("ddl_location_selector_country");
        if (country == "BH") {
            Util.hideElement('div_location_selector_zipCode');
        } else {
            Util.showElement('div_location_selector_zipCode');
        }
    }

    /**
     * Renders the page with the specified latitude and longitude.
     * @private
     * @async
     * @param {number} lat - The latitude of the location to be displayed.
     * @param {number} lng - The longitude of the location to be displayed.
     * @returns {Promise<void>}
     */
    private _renderPage = async (lat: number, lng: number): Promise<void> => {
        await this._savePosition(lat, lng);
        this._panTo();
        this._map.setZoom(this._defaultMapZoom);
        Util.showElement('div_locations');
    }

    /**
     * Returns an array of store cards based on the search criteria and favorite stores.
     * @param {Array<{ storeKey: string, name: string }> | null} favorites - The array of favorite stores to display.
     * @returns {HTMLElement[]} An array of store cards.
     */
    public getStoreCards = (favorites: { storeKey: string, name: string }[] | null): HTMLElement[] => {
        let storesCards: HTMLElement[] = [];
        const isBamahas = Common.isBahamas(this._currentLng, this._currentLat) ? "HARDCODEDBH" : null;
        let newList = this._getStoreList(isBamahas);
        if (favorites) {
            newList.forEach(s => {
                let f = null;
                if (favorites)
                    f = favorites.find(x => x.storeKey == s.storeKey);
                s.favDistance = f ? s.distance : 10000000 + s.distance;
                s.isFavorite = f ? true : false;
                s.favName = f ? f.name : "";
            });
            newList.sort((a, b) => ((a.favDistance || -1) > (b.favDistance || -1)) ? 1 : -1);
        }
        let storeIndex = 0;
        newList.forEach(s => {
            storeIndex++;
            if (s.StatusId !== StoreStausId.CLOSED)
                storesCards.push(this._getStoreCard(s, storeIndex, aOLO));
        });
        return storesCards;
    }

    private _validateZipCode = async (): Promise<void> => {
        let isValid = false;
        let zipCodeVal = Util.getElementValue('lbl_location_selector_zip_code');
        let country = Util.getElementValue("ddl_location_selector_country");
        if (country != "BH") {
            if (zipCodeVal == '') {
                DialogCreators.messageBoxOk(Names("ZipCodeRequired"), aOLO);
            } else if (!Util.isValidZipCode(zipCodeVal)) {
                DialogCreators.messageBoxOk(Names("InvalidZipCode"), aOLO);
            } else {
                this._zipCode = zipCodeVal;
                isValid = true;
            }
        } else {
            isValid = true;
            this._zipCode = '';
        }
        if (isValid) {

            if (this._zipCode == '' && country == "BH") { //set lng/lat to bahamas if no zip code and is webview and is bahamas
                this._currentLat = 25.0343;
                this._currentLng = -77.3963;
                await this._renderPage(this._currentLat, this._currentLng);
            }
            else if (this._zipCode !== '' && country !== "BH") {
                //@ts-ignore
                let gmaps = new google.maps.Geocoder();
                try {
                    let gaddr: IGeocodeResults = await gmaps.geocode({ componentRestrictions: { 'country': country, 'postalCode': zipCodeVal } });
                    if (gaddr.results.length == 0) {
                        DialogCreators.messageBoxOk("Unable to locate your position", aOLO);
                        return;
                    }

                    const locationResult = gaddr.results[0];
                    this._currentLat = locationResult.geometry.location.lat();
                    this._currentLng = locationResult.geometry.location.lng();
                    try { document.cookie = `locationData={"lat":${this._currentLat},"lng":${this._currentLng}};max-age= 3600;path=/;domain=${window.location.hostname.split('.').reverse().splice(0, 2).reverse().join('.')}`; } catch { }
                    await this._renderPage(this._currentLat, this._currentLng);
                    Util.DialogFadeOut('dia_locations_country_zip');
                }
                catch {
                    DialogCreators.messageBoxOk("Unable to locate your position", aOLO);
                }
            }


        }
    }

    /**
     * Filters the locations based on the search string.
     * @private
     * @param {string | null} search - The search string to filter stores.
     * @returns {ILocation[]} An array of filtered locations.
     */
    private _getStoreList = (search: string | null): ILocation[] => {
        if (search === 'HARDCODEDBH') { // Specific case for Bahamas
            return this._locations.filter(s => s.state === "BH");
        }

        if (!search) {
            return this._locations; // Return all locations if search is null or empty
        }

        const searchList = search.toLowerCase().split(' ');

        return this._locations.filter(location => {
            return searchList.every(searchTerm =>
                location.address1.toLowerCase().includes(searchTerm) ||
                location.address2?.toLowerCase().includes(searchTerm) ||
                location.name.toLowerCase().includes(searchTerm) ||
                location.city.toLowerCase().includes(searchTerm) ||
                location.state.toLowerCase().includes(searchTerm) ||
                location.fullState?.toLowerCase().includes(searchTerm) ||
                location.zip.toLowerCase().includes(searchTerm)
            );
        });
    }

    /**
     * Retrieves the current position of the device and renders the page using the location data.
     * If geolocation permission is denied or not granted, it shows a message box to the user.
     * @private
     * @async
     * @returns {void}
     */
    private _getPositionRenderPage = async (): Promise<void> => {
        const self = this;
        // const cookieGeolocation = Util.getGeoLocationFromCookie();
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then(async (result) => {
                if (result.state == 'granted') {
                    navigator.geolocation.getCurrentPosition(async (p) => {
                        this._currentLat = p.coords.latitude;
                        this._currentLng = p.coords.longitude;
                        await this._renderPage(this._currentLat, this._currentLng);
                    });
                } else {
                    Util.DialogFadeIn('dia_locations_country_zip', aOLO);
                    //    delete aOLO.Temp.LocationPermissionDenied;
                    //    navigator.geolocation.getCurrentPosition(async (p) => { await self._renderPage(p.coords.latitude, p.coords.longitude); });
                    //    if (aOLO.Temp.LocationPermissionTimeout) {
                    //        clearInterval(aOLO.Temp.LocationPermissionTimeout);
                    //        delete aOLO.Temp.LocationPermissionTimeout;
                    //    }
                    //}
                    //else if (result.state == 'prompt') {
                    //    if (!aOLO.Temp.LocationPermissionTimeout) {
                    //        aOLO.Temp.LocationPermissionDenied = true;
                    //        await self._renderPage(39.8283, -98.5795);
                    //        await self._waitForLocation();
                    //    }
                    //}
                    //else {
                    //    if (!aOLO.Temp.LocationPermissionTimeout) {
                    //        aOLO.Temp.LocationPermissionDenied = true;
                    //        await self._renderPage(39.8283, -98.5795);
                    //        DialogCreators.messageBoxOk(Names("LocationServicesBlocked"), aOLO,
                    //            () => {
                    //                aOLO.Temp.LocationPermissionTimeout = setInterval(async () => { await self._getPosition(); }, 50);
                    //            });
                    //    }
                }
            });
        } else if (navigator.geolocation) {
            //    }
            //    else if (navigator.geolocation) {
            //        navigator.geolocation.getCurrentPosition(
            //            async (position) => { 
            //                Util.saveGeoLocationToCookie(position.coords.latitude, position.coords.longitude);
            //                await self._renderPage(position.coords.latitude, position.coords.longitude);
            //            },
            //            async (error) => { 
            //                if (error.code === error.PERMISSION_DENIED) {
            //                    await self._renderPage(39.8283, -98.5795); 
            //                }
            //            }
            //        );
            //    }

            //}

            ///**
            // * Waits for the location to be retrieved and then renders the page using the location data.
            // * @private
            // * @async
            // * @returns {void}
            // */
            //private _waitForLocation = async (): Promise<void> => {
            //    if (Util.isWebView()) {
            //        await getMobileLocation();
            //        setTimeout(async () => {
            //            if (Common.isWebviewLocationDenied()) {
            //                await this._renderPage(this._mobileLat, this._mobileLng);
            //            }
            //            else {
            //                await this._renderPage(aOLO.Temp.Mobile.lat, aOLO.Temp.Mobile.lng);
            //            }
            //        }, 1000);
            //    } else {
            navigator.geolocation.getCurrentPosition(async (p) => {
                this._currentLat = p.coords.latitude;
                this._currentLng = p.coords.longitude;
                await this._renderPage(this._currentLat, this._currentLng);
            });
            // } else if (cookieGeolocation !== null) {
            //     await self._renderPage(cookieGeolocation.latitude, cookieGeolocation.longitude);
        } else {
            Util.DialogFadeIn('dia_locations_country_zip', aOLO);
        }

    }

    ///**
    // * Waits for the location to be retrieved and then renders the page using the location data.
    // * @private
    // * @async
    // * @returns {void}
    // */
    //private _waitForLocation = async (): Promise<void> => {
    //    if (this._mobileApp) {
    //        await this._getMobileLocation();
    //        setTimeout(async () => {
    //            if (Common.isWebviewLocationDenied()) {
    //                await this._renderPage(this._currentLat, this._currentLng);
    //            }
    //            else {
    //                await this._renderPage(aOLO.Temp.MobileLocation.lat, aOLO.Temp.MobileLocation.lng);
    //            }
    //        }, 1000);
    //    } else {
    //        navigator.geolocation.getCurrentPosition(async (p) => {
    //            await this._renderPage(p.coords.latitude, p.coords.longitude);
    //        });
    //    }
    //}

    /**
     * Saves the current position of the user and updates the displayed stores accordingly.
     * @private
     * @async
     * @param {number} lat - The latitude of the current position.
     * @param {number} lng - The longitude of the current position.
     * @returns {Promise<void>} A promise that resolves when the new position has been saved and the stores have been updated.
     */
    private _savePosition = async (lat: number, lng: number): Promise<void> => {
        this._currentLat = lat;
        this._currentLng = lng;
        // Util.saveGeoLocationToCookie(lat, lng);
        this._calculateDistance();
        let favoriteStores = await aOLO.ProfileUser?.getFavoriteStores() || [];
        this.listStores(favoriteStores);
        if (!this._map)
            await this._loadGoogleMap();
    }

    /**
     * Pans the map to the first location in the `_locations` array.
     * @private
     * @returns {void}
     */
    private _panTo = (): void => {
        let location = {
            lat: this._currentLat != 0 ? this._currentLat : 39.8283,
            lng: this._currentLng != 0 ? this._currentLng : -98.5795,
            type: "User"
        };
        this._map.panTo(location);
    }

    /**
     * Displays the store cards for the given search string and favorite stores.
     * @private
     * @param {Array<{storeKey: string, name: string}>|null} favorites - An array of favorite store objects, or `null` if there are no favorites.
     * @returns {void}
     */
    public listStores = (favorites: { storeKey: string, name: string }[] | null) => {
        let storeCards = this.getStoreCards(favorites);
        Common.GUI_Empty("div_locations_list");
        let mapList = document.getElementById("div_locations_list");
        storeCards.forEach(x => {
            if (mapList)
                mapList.appendChild(x);
        });
        this._setStoreCardEvents();
    }

    /**
     * Sets click event listeners on store card buttons and store favorite divs. Clicking a store card button will trigger 
     * the _startOrder method with the store key as a parameter. Clicking a store favorite div will trigger the 
     * _favoriteStoreOnClick method with the store key as a parameter.
     * @private
     * @returns {void}
     */
    private _setStoreCardEvents = (): void => {
        const storeCardBtns = document.getElementsByName("btn_locations_store_cards");
        storeCardBtns.forEach(button => {
            Util.setElement("onclick", button, async () => {
                const storeUrl = button.dataset.url || "";
                if (storeUrl.includes('granburyrs.com')) {
                    if (this._mobileApp)
                        window.location.href = storeUrl;
                    else
                        window.open(storeUrl, '_blank');
                    return;
                }

                const url = await this._createUrlAsync(storeUrl, button.dataset.sky || "", aOLO);
                if (this._mobileApp)
                    this._mobileUrlRedirect(url, button.dataset.sky || "", aOLO);
                else
                    window.open(url, '_blank');
            })
        });

        if (aOLOModules.LoyaltyProvider.getLoyaltyProvider() == LoyaltyProvider.PUNCHH)
            return;

        const storeFavoriteDivs = document.getElementsByName("div_locations_store_favorites");
        storeFavoriteDivs.forEach(div => {
            Util.setElement("onclick", div, () => { this._favoriteStoreOnClick(div.dataset.sky || "") })
        });
    }

    /**
     * Calculates the distance between the user's current location and the locations in this._locations array.
     * If the user's current location has been set, the method will update each location's distance property 
     * and sort the locations array based on the distances in ascending order.
     * @private
     * @returns {void}
     */
    private _calculateDistance = (): void => {
        if (this._currentLat !== 0 && this._currentLng !== 0) {
            this._locations.forEach(x => {
                x.distance = Util.Float2(Util.GetDistance(this._currentLat, this._currentLng, x.latitude, x.longitude, "M"));
            });
            this._locations.sort((a, b) => (a.distance > b.distance) ? 1 : -1);

            let locationList = document.getElementById("div_locations_list");

            if (locationList)
                locationList.style.gridTemplateRows = `repeat(${this._locations.length}, 1fr)`;

            let storeIndex: number = 0;
            this._locations.forEach(x => {
                let distanceSpan = document.getElementById(`spn_locations_store_distance_${x.storeKey}`);
                if (distanceSpan)
                    distanceSpan.innerText = `${x.distance}`;

                storeIndex++;

                let content = storeIndex > 99 ? '99+' : storeIndex;

                let storeDiv = document.getElementById(`div_locations_store_${x.storeKey}`);
                if (storeDiv)
                    storeDiv.style.gridRow = `${storeIndex}`;

                let storeIndexDiv = document.getElementById(`div_locations_store_index_${x.storeKey}`);
                if (storeIndexDiv)
                    storeIndexDiv.innerText = `${content}`;

                if (this._markers) {
                    let marker = this._markers.find((m: any) => m.storeKey === x.storeKey);
                    if (marker) {
                        //@ts-ignore
                        const pinGlyph = new google.maps.marker.PinElement({
                            glyph: `${content}`,
                            glyphColor: "white",
                        });
                        marker.content = pinGlyph.element;
                    }
                }



            });
        }
    }

    /**
     * Returns a store card element for the given store.
     * @private
     * @param {ILocation} store - The store to create a card for.
     * @returns {HTMLElement} - The store card element.
     */
    private _getStoreCard = (store: ILocation, index: number, localAolo: IaOLO): HTMLElement => {
        let heart = store.isFavorite ? "<span class='icon-X-HeartFilled heart'></span>" : "<span class='icon-W-Heart heart'></span>";
        // Hide unfilled hearts for Punchh until next phase
        if (aOLOModules.LoyaltyProvider.getLoyaltyProvider() == LoyaltyProvider.PUNCHH && !store.isFavorite)
            heart = "";

        let favoriteName = store.isFavorite ? store.favName + "<br/>" : "";
        let btnClass = Util.getElementValue("hf_locations_btn_class");
        let hideFav = (aOLO.User.ProfileId == 0 ? "hidden" : "");
        let storeButton = "";
        let storeStatus = "";
        let unitsShort = "mile_short";
        let storeDistance = store.distance;
        if (localAolo.storeInfo.Country.UnitsOfMeasurement == "Metric") {
            storeDistance = Util.Float2(Util.convertMilesToKilometers(storeDistance));
            unitsShort = "kilometer_short";
        }
        switch (store.StatusId) {
            case StoreStausId.ACTIVE:
                storeButton = `<button id="btn_locations_store_card_${store.storeKey}" name="btn_locations_store_cards" class="orderButton ${btnClass}" ltag="StartOrder" data-sky="${store.storeKey}" data-url="${store.OnlineOrderingURL}">${Names("StartOrder")}</button>`;
                break;
            case StoreStausId.TEMPORARILY_CLOSED:
                storeStatus = `<div class="locations-store-status" ltag="TemporarilyClosed">${Names("TemporarilyClosed")}</div>`;
                break;
            case StoreStausId.INACTIVE:
                storeStatus = `<div class="locations-store-status" ltag="Inactive">${Names("Inactive")}</div>`;
                break;
            case StoreStausId.COMING_SOON:
                storeStatus = `<div class="locations-store-status" ltag="ComingSoon">${Names("ComingSoon")}</div>`;
                break;
        }
        let content = index > 99 ? '99+' : index;

        const tempAddress: IAddressFormat = {
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        };

        const addressObject = Util.formatAddressObject(tempAddress, aOLO);

        let storeItem =
            `<div id="div_locations_store_${store.storeKey}" class="locations-store hidden" style = "grid-row: ${index};">\
                <div class="locations-store-info">\
                    <div class="info">\
                        <div class='gridColsAuto1x'>\
                            <div class='gridCol1 location-store-marker'>\
                                <svg xmlns="http://www.w3.org/2000/svg" width="26px" height="38px" viewBox="0 0 26 37" style="display: block; overflow: visible; grid-area: 1;"><g fill="none" fill-rule="evenodd" style="pointer-events: auto;"><path class="RIFJN-maps-pin-view-border" d="M13 0C5.8175 0 0 5.77328 0 12.9181C0 20.5733 5.59 23.444 9.55499 30.0784C12.09 34.3207 11.3425 37 13 37C14.7225 37 13.975 34.2569 16.445 30.1422C20.085 23.8586 26 20.6052 26 12.9181C26 5.77328 20.1825 0 13 0Z" fill="var(--btnColorBg)"></path><path class="RIFvHW-maps-pin-view-background" fill="var(--btnColorBg)" d="M13.0167 35C12.7836 35 12.7171 34.9346 12.3176 33.725C11.9848 32.6789 11.4854 31.0769 10.1873 29.1154C8.92233 27.1866 7.59085 25.6173 6.32594 24.1135C3.36339 20.5174 1 17.7057 1 12.6385C1.03329 6.19808 6.39251 1 13.0167 1C19.6408 1 25 6.23078 25 12.6385C25 17.7057 22.6699 20.55 19.6741 24.1462C18.4425 25.65 17.1443 27.2193 15.8793 29.1154C14.6144 31.0442 14.0818 32.6135 13.749 33.6596C13.3495 34.9346 13.2497 35 13.0167 35Z"></path><path class="KWCFZI-maps-pin-view-default-glyph" d="M13 18C15.7614 18 18 15.7614 18 13C18 10.2386 15.7614 8 13 8C10.2386 8 8 10.2386 8 13C8 15.7614 10.2386 18 13 18Z" fill="white" style="display: none;"></path></g></svg>\
                                <div id="div_locations_store_index_${store.storeKey}" class='location-store-marker-content'>${content}</div>\
                            </div>\
                            <div class="locations-address gridCol2">\
                                <span>${favoriteName}</span>\
                                <span>${addressObject.address1}</span>\
                                ${addressObject.address3 ? `<br/><span>${addressObject.address3}</span>` : ``}
                                <br/>\
                                <span>${addressObject.cityState}</span>\
                            </div>\
                        </div>\
                        <div>${storeButton}</div>\
                    </div>\
                    <div class="heartDistance">\
                        <div class="distance-unit"><span id="spn_locations_store_distance_${store.storeKey}" >${storeDistance}</span> <span ltag="${unitsShort}">${Names(unitsShort)}</span></div>\
                        <div id="div_locations_store_favorite_${store.storeKey}" name="div_locations_store_favorites" data-sky="${store.storeKey}" class="heart ${hideFav}">${heart}</div>\
                    </div>
                </div>
                ${storeStatus}
            </div>`;

        let div = Util.createHtmlElementFromTemplate(storeItem);
        //div.onmouseover = () => {
        //    store.polies?.forEach((p: any) => {
        //        p.setOptions({ fillOpacity: 0.70 });
        //    });
        //    let location = {
        //        lat: store.latitude,
        //        lng: store.longitude,
        //        type: "User"
        //    };
        //    this._map.panTo(location);
        //}
        //div.onmouseout = () => {
        //    store.polies?.forEach((p: any) => {
        //        p.setOptions({ fillOpacity: 0.35 });
        //    });
        //}
        div.onclick = () => {
            let location = {
                lat: store.latitude,
                lng: store.longitude,
                type: "User"
            };
            this._map.panTo(location);
            this._map.zoom = 12;
        }

        return div;
    }

    /**
     * Loads the Google Map and initializes the map instance.
     * @private
     * @returns {void}
     */
    private _loadGoogleMap = async (): Promise<void> => {
        const self = this;
        //@ts-ignore
        /*
        this._map = new google.maps.Map(document.getElementById("div_locations_map") as HTMLElement, {
            zoom: (aOLO.Temp.LocationPermissionDenied) ? 4 : this._defaultMapZoom,
            gestureHandling: 'greedy',
            scaleControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            disableDefaultUI: true
        });
        this._mapAddMarkers();
        */

        //@ts-ignore
        const { Map, InfoWindow } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        //@ts-ignore
        const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        this._map = new Map(
            document.getElementById("div_locations_map") as HTMLElement,
            {
                zoom: (aOLO.Temp.LocationPermissionDenied) ? 4 : this._defaultMapZoom,
                //center: { lat: -28.024, lng: 140.887 },
                mapId: 'Stores_Location_Map',
                gestureHandling: 'greedy',
                scaleControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                disableDefaultUI: true
            }
        );

        this._infoWindow = new InfoWindow({
            content: "Info window content",
            disableAutoPan: true,
        });

        let storeIndex = 0;
        this._markers = this._locations.map((store, i) => {
            storeIndex++;
            let content = storeIndex > 99 ? '99+' : storeIndex;
            const storePosition = {
                lat: store.latitude,
                lng: store.longitude,
                type: "User"
            };

            const pinGlyph = new PinElement({
                glyph: `${content}`,
                glyphColor: "white",
            });

            let marker = new AdvancedMarkerElement({
                position: storePosition,
                content: pinGlyph.element,
            });
            marker.storeKey = store.storeKey;
            marker.addListener("click", async () => {
                const storeUrl = store.OnlineOrderingURL;
                if (storeUrl.includes('granburyrs.com')) {
                    if (this._mobileApp)
                        window.location.href = storeUrl;
                    else
                        window.open(storeUrl, '_blank');
                    return;
                }

                const url = await this._createUrlAsync(storeUrl, store.storeKey, aOLO);
                if (this._mobileApp)
                    this._mobileUrlRedirect(url, store.storeKey, aOLO);
                else
                    window.open(url, '_blank');
            });
            return marker;
        });
        const markers = this._markers;
        const map = this._map;
        this._markerCluster = new MarkerClusterer({ markers, map });
        this._map.addListener('idle', () => {
            this._mapShowLocations();
        });

        const input = document.getElementById("txt_locations_search") as HTMLInputElement;
        //@ts-ignore
        const searchBox = new google.maps.places.SearchBox(input);

        // Bias the SearchBox results towards current map's viewport.
        this._map.addListener("bounds_changed", () => {
            //@ts-ignore
            searchBox.setBounds(self._map.getBounds() as google.maps.LatLngBounds);
        });

        searchBox.addListener("places_changed", () => {
            const places: any = searchBox.getPlaces();
            if (places.length == 0)
                return;

            // For each place, get the icon, name and location.
            //@ts-ignore
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(places[0].geometry.location);

            this._currentLat = places[0].geometry.location.lat();
            this._currentLng = places[0].geometry.location.lng();
            this._calculateDistance();

            this._map.fitBounds(bounds);
            //this._map.setZoom((aOLO.Temp.LocationPermissionDenied) ? 4 : this._defaultMapZoom);
            this._map.setZoom(this._defaultMapZoom);
        });
    }

    /**
     * Adds markers to the Google Map for each location in the Locations object.
     * @private
     * @returns {void}
     */
    private _mapAddMarkers = (): void => {
        //let sIndex = 0;
        //this._locations.forEach(s => {
        //    let color = this._zoneColors[sIndex]
        //    this._mapAddMarker(s, color);
        //    sIndex++;
        //    sIndex = sIndex % 10;
        //});

        //@ts-ignore


    }

    /**
     * Adds a marker to the Google Maps instance for a given store.
     * @private
     * @param {ILocation} store - The store to add the marker for.
     * @param {string} color - The color to use for the store's zone polygon.
     * @returns {void}
     */
    private _mapAddMarker = (store: ILocation, color: string): void => {
        let location = {
            lat: store.latitude,
            lng: store.longitude,
            type: "User"
        };

        //@ts-ignore
        store.marker = new google.maps.Marker({
            position: location,
            map: this._map,
            icon: "/favicon.png"
        });

        //@ts-ignore
        google.maps.event.addDomListener(store.marker, 'click', async () => {
            const storeUrl = store.OnlineOrderingURL;
            if (storeUrl.includes('granburyrs.com')) {
                if (this._mobileApp)
                    window.location.href = storeUrl;
                else
                    window.open(storeUrl, '_blank');
                return;
            }

            const url = await this._createUrlAsync(storeUrl, store.storeKey, aOLO);
            if (this._mobileApp)
                this._mobileUrlRedirect(url, store.storeKey, aOLO);
            else
                window.open(url, '_blank');
        });

        this._setStoreZone(store, color);
    }

    /**
     * Sets the store zone polygon on the Google Maps instance.
     * @private
     * @param {ILocation} store - The store to set the zone for.
     * @param {string} fillColor - The fill color to use for the polygon.
     * @returns {void}
     */
    private _setStoreZone = (store: ILocation, fillColor: string): void => {
        store.polies = [];
        store.polygons.forEach(p => {
            let points: { lat: number, lng: number }[] = [];
            p.forEach(c => {
                let iLat = c[1];
                let iLng = c[0];
                if (iLat < 0) {
                    iLat = c[0];
                    iLng = c[1];
                }
                let pos = {
                    lat: iLat,
                    lng: iLng
                };
                points.push(pos);
            });
            //@ts-ignore
            let poly = new google.maps.Polygon({
                paths: points,
                strokeColor: fillColor,
                strokeOpacity: 0.7,
                strokeWeight: 2,
                fillColor: fillColor,
                fillOpacity: 0.35
            });
            poly.setMap(this._map);

            store.polies.push(poly);
            //@ts-ignore
            google.maps.event.addListener(poly, "mouseover", function () {
                return function (storeKey) {
                    poly.setOptions({ fillOpacity: 0.70 });
                    let div = document.getElementById(`div_locations_store_${storeKey}`);
                    Util.setElementClass("add", `div_locations_store_${storeKey}`, "locations-hovered-store");
                    let topPos = div ? div.offsetTop : 0;
                    let searchBox = document.getElementById("div_locations_search_box");
                    let storeList = document.getElementById("div_locations_search_container");
                    let scrollOptionsSmooth: ScrollToOptions = {
                        left: 0,
                        top: topPos - (storeList ? storeList.offsetTop : 0) - (searchBox ? searchBox.offsetHeight : 0),
                        behavior: 'smooth'
                    };
                    if (storeList)
                        storeList.scrollTo(scrollOptionsSmooth);
                }(store.storeKey);
            });

            //@ts-ignore
            google.maps.event.addListener(poly, "mouseout", function () {
                return function (storeKey) {
                    poly.setOptions({ fillOpacity: 0.35 });
                    Util.setElementClass("remove", `div_locations_store_${storeKey}`, "locations-hovered-store");
                }(store.storeKey);
            });
            let self = this;
            //@ts-ignore
            google.maps.event.addListener(poly, "click", async () => {
                const storeUrl = store.OnlineOrderingURL;
                if (storeUrl.includes('granburyrs.com')) {
                    if (this._mobileApp)
                        window.location.href = storeUrl;
                    else
                        window.open(storeUrl, '_blank');
                    return;
                }

                const url = await this._createUrlAsync(storeUrl, store.storeKey, aOLO);
                if (this._mobileApp)
                    this._mobileUrlRedirect(url, store.storeKey, aOLO);
                else
                    window.open(url, '_blank');
            });

        });
    }

    private _mapShowLocations() {
        let center = this._map.getCenter();
        this._currentLat = center.lat();
        this._currentLng = center.lng();
        this._calculateDistance();

        let bounds = this._map.getBounds();
        let storeCount = 0;
        this._markers.forEach((x: any) => {
            if (bounds.contains(x.position) === true) {
                Util.showElement(`div_locations_store_${x.storeKey}`);
                storeCount++;
            } else
                Util.hideElement(`div_locations_store_${x.storeKey}`);
        });
        if (storeCount == 0) {
            let zoom = this._map.getZoom();
            if (zoom > 4)
                this._map.setZoom(zoom - 2);
        }
    }

    /**
     * Initiates the ordering process for a given store.
     * @private
     * @param {string} storeKey - The unique key of the store to start the order for.
     * @returns {void}
     */
    private _createUrlAsync = async (storeUrl: string, storeKey: string, localAolo: IaOLO): Promise<string> => {
        let url = "";
        if (localAolo.Temp.BrandOfferCode)
            url = await this._getCampaignUrl(storeKey, localAolo);
        else {
            let ssoToken: string | null = null
            if (aOLO.ProfileUser?.isLoggedIn() && aOLOModules.LoyaltyProvider.getLoyaltyProvider() !== LoyaltyProvider.PUNCHH)
                ssoToken = await aOLOModules.LoyaltyProvider.generateSsoTokenAsync(storeKey);
            url = `${storeUrl}${storeUrl.endsWith("/") ? "" : "/"}?id=${storeKey}${ssoToken ? `&token=${ssoToken}` : ""}`;
        }

        if (localAolo.specialCode)
            url += `&specialcode=${localAolo.specialCode}`;

        if (localAolo.reOrderId) {
            url += `&reorder=${localAolo.reOrderId}`;
            delete localAolo.reOrderId;
        }

        if (localAolo.orderCouponId) {
            url += `&ordercoupon=${localAolo.orderCouponId}`;
            delete localAolo.orderCouponId;
        }

        return url;
    }

    private _mobileUrlRedirect(url: string, storeKey: string, localAolo: IaOLO) {
        url += "&mobile=true";
        const tempLoct = this._locations.find(l => l.storeKey === storeKey);
        if (tempLoct) {
            if (tempLoct.distance > 25) {
                let customerDistance = tempLoct.distance;
                let messageName = "XtraMiles";
                if (true) {
                    customerDistance = Util.Float2(Util.convertMilesToKilometers(customerDistance));
                    messageName = "XtraKilometers";
                }
                DialogCreators.messageBox(Names(messageName).replace("??", `<div class='warning bold inline'>${customerDistance}</div>`), localAolo, [
                    { "text": Names("YesProceed"), "callBack": () => { this._yesCallBack(url); } },
                    { "text": Names("NoOtherStore"), "callBack": this._noCallBack }]);
            } else {
                this._yesCallBack(url)
            }
        }
    }

    private _yesCallBack = (url: string): void => {
        window.location.href = url;
    }

    private _noCallBack = (): void => {
        //this._waitForLocation();
    }

    private _getCampaignUrl = async (storeKey: string, localAolo: IaOLO): Promise<string> => {
        const baseUrl = `${location.origin}/?id=${storeKey}`;
        const code = localAolo.User.Offers?.Codes.find(x => x.CouponCode == localAolo.Temp.BrandOfferCode);
        if (!code)
            return baseUrl;

        const payload: IGetOfferUrlModel = {
            StoreKey: storeKey,
            ProfileId: localAolo.User.ProfileId,
            CouponCode: code.CouponCode,
            IsCampaignCode: !code.IsCouponPromotionCode
        };

        const couponUrl = await new BrandService().getOfferUrl(payload, localAolo);
        const url = (couponUrl.url != "") ? `${location.origin}/?coupon=${couponUrl.url}` : baseUrl;
        return url;
    }

    /**
     * Handles the click event for the favorite store icon.
     * If the user is not signed in, a message box is displayed asking the user to sign in.
     * If the user is signed in, the method retrieves the user's favorite stores and determines whether the clicked store is already favorited.
     * If the clicked store is already favorited, a message box is displayed asking the user if they want to remove the store from their favorites.
     * If the clicked store is not already favorited, an input box is displayed asking the user to enter a name for the store and to check whether they want the store to be the default. 
     * Otherwise, the store is added to the user's favorites with the given name and default flag.
     * @private
     * @async
     * @param {string} storeKey - The unique key for the store that was clicked.
     * @returns {Promise<void>} A promise that resolves with no value.
     */
    private _favoriteStoreOnClick = async (storeKey: string): Promise<void> => {
        const self = this;
        if (!aOLO.ProfileUser) {
            DialogCreators.messageBox(Names("Locations_Heart_SaveLocationName_NotSignedIn"), aOLO, [
                {
                    "text": Names("Yes"), "callBack": () => {
                        if (self._pageFunction)
                            self._pageFunction("signin", true);
                    }
                },
                { "text": Names("No"), "callBack": null }
            ]);
        } else {
            const favoriteStores = aOLO.ProfileUser.getFavoriteStores();
            if (favoriteStores.find(x => x.storeKey == storeKey))
                DialogCreators.messageBox(Names("Locations_Heart_SaveLocationName_RemoveHeart"), aOLO, [
                    { "text": Names("Yes"), "callBack": () => { self._deleteFavoriteStore(storeKey, aOLO); } },
                    { "text": Names("No"), "callBack": null }
                ]);
            else {
                let isDefault = null;
                if (aOLO.User.FavoriteStores && aOLO.User.FavoriteStores.length) {
                    isDefault = aOLO.User.DefaultStoreKey?.toLowerCase() === storeKey.toLowerCase();
                }

                DialogCreators.InputBox(Names("Locations_Heart_SaveLocationName_SignedIn"),
                    false,
                    null,
                    null,
                    aOLO,
                    [{
                        text: Names("Confirm"), close: true, callBack: () => {
                            const input = Util.getElementValue('txt_inputbox');
                            const checkbox = document.getElementById('chk_inputbox') as HTMLInputElement;
                            self._saveFavoriteStoreName(storeKey, input, isDefault ? checkbox.checked : true, aOLO);
                        }
                    }, {
                        text: Names("Cancel"), close: true, callBack: null
                    }], isDefault, Names('MakeMyDefaultStore'));
            }
        }
    }

    /**
     * Saves a store to the user's favorites with the given name. Sets the user's new DefaultStoreKey
     * @private
     * @async
     * @param {string} storeKey - The unique key for the store to save.
     * @param {string} name - The name to give the saved store.
     * @param {boolean} isDefaultStore - Flag for if the store is the default or not
     * @returns {Promise<void>} A promise that resolves with no value.
     */
    private _saveFavoriteStoreName = async (storeKey: string, name: string, isDefaultStore: boolean, localAolo: IaOLO): Promise<void> => {
        localAolo.User.DefaultStoreKey = storeKey;
        const result = await localAolo.ProfileUser?.saveFavoriteStore(storeKey, name, isDefaultStore) || false;
        await this._populateFavoriteStores(result, "favoriteStoreSaveName_Error", localAolo);
        this._mapShowLocations();
    }

    /**
     * Deletes a store from the user's favorites.
     * @private
     * @async
     * @param {string} storeKey - The unique key for the store to delete.
     * @returns {Promise<void>} A promise that resolves with no value.
     */
    private _deleteFavoriteStore = async (storeKey: string, localAolo: IaOLO): Promise<void> => {
        const result = await localAolo.ProfileUser?.deleteFavoriteStore(storeKey) || false;
        await this._populateFavoriteStores(result, "favoriteStoreDelete_Error", localAolo);
        this._mapShowLocations();
    }

    /**
     * Populates the location list with the user's favorite stores or an error message.
     * @private
     * @async
     * @param {boolean} response - A boolean value indicating whether the operation was successful.
     * @param {string} errorMessageID - The ID of the error message to display in case the operation fails.
     * @returns {Promise<void>} A Promise that resolves when the method finishes executing.
     */
    private _populateFavoriteStores = async (response: boolean, errorMessageID: string, localAolo: IaOLO): Promise<void> => {
        if (response) {
            const favoriteStores = localAolo.ProfileUser?.getFavoriteStores() || [];
            this._populateLocationList(favoriteStores);
        } else
            DialogCreators.messageBoxOk(Names(errorMessageID), localAolo);
    }

    /**
     * Populates the location list with stores matching a given search term and/or the user's favorite stores.
     * @private
     * @param {{storeKey: string, name: string}[]|null} favorites - An array of objects representing the user's favorite stores.
     * @returns {void} Returns nothing.
     */
    private _populateLocationList = (favorites: { storeKey: string, name: string }[] | null = null): void => {
        let storeCards = this.getStoreCards(favorites);
        Common.GUI_Empty("div_locations_list");
        let mapList = document.getElementById("div_locations_list");
        storeCards.forEach(x => {
            if (mapList)
                mapList.appendChild(x);
        });
        this._setStoreCardEvents();
    }

    ///**
    // * Executes a search and populates the location list with the results.
    // * @private
    // * @async
    // * @returns {Promise<void>} A Promise that resolves when the method finishes executing.
    // */
    //private _search = async (): Promise<void> => {
    //    let search = Util.getElementValue("txt_locations_search");
    //    const stores = this._getStoreList(search);
    //    let storeMap = new Map(stores.map(store => [store.storeKey, store]));

    //    this._locations.forEach(location => {
    //        if (storeMap.has(location.storeKey)) {
    //            Util.showElement(`div_locations_store_${location.storeKey}`);
    //            storeMap.delete(location.storeKey);
    //        } else
    //            Util.hideElement(`div_locations_store_${location.storeKey}`);
    //    });
    //}

    private _setFullStateName(): void {
        const usStates: { [abbreviation: string]: string } = {
            AL: 'Alabama',
            AK: 'Alaska',
            AZ: 'Arizona',
            AR: 'Arkansas',
            CA: 'California',
            CO: 'Colorado',
            CT: 'Connecticut',
            DE: 'Delaware',
            FL: 'Florida',
            GA: 'Georgia',
            HI: 'Hawaii',
            ID: 'Idaho',
            IL: 'Illinois',
            IN: 'Indiana',
            IA: 'Iowa',
            KS: 'Kansas',
            KY: 'Kentucky',
            LA: 'Louisiana',
            ME: 'Maine',
            MD: 'Maryland',
            MA: 'Massachusetts',
            MI: 'Michigan',
            MN: 'Minnesota',
            MS: 'Mississippi',
            MO: 'Missouri',
            MT: 'Montana',
            NE: 'Nebraska',
            NV: 'Nevada',
            NH: 'New Hampshire',
            NJ: 'New Jersey',
            NM: 'New Mexico',
            NY: 'New York',
            NC: 'North Carolina',
            ND: 'North Dakota',
            OH: 'Ohio',
            OK: 'Oklahoma',
            OR: 'Oregon',
            PA: 'Pennsylvania',
            RI: 'Rhode Island',
            SC: 'South Carolina',
            SD: 'South Dakota',
            TN: 'Tennessee',
            TX: 'Texas',
            UT: 'Utah',
            VT: 'Vermont',
            VA: 'Virginia',
            WA: 'Washington',
            WV: 'West Virginia',
            WI: 'Wisconsin',
            WY: 'Wyoming'
        };

        const countries: { [abbreviation: string]: string } = {
            BH: 'Bahamas',
            NN: 'Nassau',
            PR: 'Puerto Rico',
        };

        for (const location of this._locations) {
            const fullState = usStates[location.state.toUpperCase()];
            if (fullState)
                location.fullState = fullState;
            else
                location.fullState = countries[location.state.toUpperCase()];
        }
    }

    public GetLocations() {
        return this._locations;
    }

}